// Promo code + Validate + "Book Fitting" CTA — the lower-right of the revealed panel.
//
// The Validate button calls POST /promos/validate (a new endpoint, pending
// in the middleware) with the current code plus the selected fitting and
// location context. On success, the response carries discount details that
// are shown inline below the input. The user can still proceed to book
// without validating — validation is informational, not gating.
//
// State transitions live in the reducer:
//   - SET_PROMO_CODE: editing the code clears any prior validation result
//   - VALIDATE_PROMO_START / SUCCESS / ERROR: standard request lifecycle

import styles from '../styles/widget.module.css';
import { validatePromo } from '../api/client';
import { parsePrice } from '../utils/pricing';
import type { BookingAction, BookingState } from '../state/machine';
import type { PromoDiscount } from '../types';

interface PromoConfirmProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onConfirmFlow: () => void;
}

export function PromoConfirm({ state, dispatch, onConfirmFlow }: PromoConfirmProps) {
  const canSubmit =
    state.selectedLocation &&
    state.selectedFittingType &&
    state.selectedDate &&
    state.selectedSlot;

  const canValidate =
    state.promoCode.trim().length > 0 &&
    !state.promoValidating &&
    // Don't re-validate the same code we just validated successfully.
    state.promoValidatedCode !== state.promoCode.trim();

  const handleValidate = async () => {
    const code = state.promoCode.trim();
    if (!code) return;
    // SF /PromoValidate needs store + fitting + slot + price to evaluate
    // eligibility. The Validate button is only enabled once all four are
    // present, but typescript can't see that from canValidate alone.
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedSlot
    )
      return;

    dispatch({ type: 'VALIDATE_PROMO_START' });
    try {
      const res = await validatePromo({
        promoCode: code,
        locationId: state.selectedLocation.id,
        fittingTypeName: state.selectedFittingType.name,
        appointmentStartDateTime: state.selectedSlot.start,
        price: parsePrice(state.selectedFittingType.pricingLabel),
        email: state.email.trim() || undefined,
      });
      dispatch({
        type: 'VALIDATE_PROMO_SUCCESS',
        payload: {
          code,
          valid: res.valid,
          discount: res.discount,
          message: res.message,
        },
      });
    } catch (err) {
      dispatch({
        type: 'VALIDATE_PROMO_ERROR',
        payload: err instanceof Error ? err.message : 'Could not validate promo code',
      });
    }
  };

  return (
    <>
      <div className={`${styles.field} ${styles.promoCodeField}`}>
        <label className={styles.label} htmlFor="cc-promo">Promo Code (Optional)</label>
        <div className={styles.promoInputRow}>
          <input
            id="cc-promo"
            className={styles.input}
            type="text"
            value={state.promoCode}
            onChange={(e) => dispatch({ type: 'SET_PROMO_CODE', payload: e.target.value })}
            placeholder="Enter promo code"
          />
          <button
            type="button"
            className={styles.buttonOutline}
            disabled={!canValidate}
            data-cc-track="cta"
            data-cc-track-id="validate_promo"
            onClick={handleValidate}
          >
            {state.promoValidating ? (
              <>
                <span className={styles.spinner} />
                Checking…
              </>
            ) : (
              'Validate'
            )}
          </button>
        </div>
        <PromoValidationMessage state={state} />
      </div>
      <button
        className={`${styles.button} ${styles.stepButtonMobile}`}
        disabled={!canSubmit}
        data-cc-track="cta"
        data-cc-track-id="book_fitting"
        onClick={onConfirmFlow}
      >
        Book Fitting
      </button>
    </>
  );
}

/**
 * Inline status message for the promo field. Three cases:
 *   - network/transport error (e.g., endpoint not yet available)
 *   - valid promo with discount details
 *   - invalid promo with optional server message
 * Nothing renders if no validation has happened.
 */
function PromoValidationMessage({ state }: { state: BookingState }) {
  if (state.promoValidationError) {
    return (
      <p className={styles.promoStatusError}>
        Couldn’t validate right now — {state.promoValidationError}
      </p>
    );
  }

  if (!state.promoValidation) return null;

  if (!state.promoValidation.valid) {
    return (
      <p className={styles.promoStatusError}>
        {state.promoValidation.message ?? 'This promo code isn’t valid for the selected service.'}
      </p>
    );
  }

  return (
    <p className={styles.promoStatusOk}>
      ✓ Promo applied{formatDiscount(state.promoValidation.discount)}
    </p>
  );
}

function formatDiscount(d?: PromoDiscount): string {
  if (!d) return '';
  const currency = d.currency ?? 'USD';
  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
      n,
    );

  if (typeof d.discountedValue === 'number') {
    if (typeof d.originalValue === 'number' && d.originalValue > d.discountedValue) {
      return ` — service price now ${fmt(d.discountedValue)} (was ${fmt(d.originalValue)})`;
    }
    return ` — service price now ${fmt(d.discountedValue)}`;
  }
  if (typeof d.amount === 'number') return ` — ${fmt(d.amount)} off`;
  if (typeof d.percent === 'number') return ` — ${d.percent}% off`;
  return '';
}
